import * as React from 'react';
import moment from 'moment';
import { Container, Table } from 'react-bootstrap';
import { shallowEqual } from 'react-redux';

import { ethnicityMap, resolveEthnicityLabel } from "../../../libs/dataLib";
import { useAppSelector } from '../../../redux/store';
import { BirthdateDisplayType, DateFormatType, PatientDemographics } from '../../../Types/Common/Types';
import { convertInchesToCentimeters, convertPoundsToKilograms, isEmptyObjectOrArray } from '../../../libs/utilsLib';

interface TrialsDemographicsTableProps {
  demographics: PatientDemographics | undefined;
  dateFormat: DateFormatType;
  birthdateDisplay: BirthdateDisplayType;
  disableEthnicity?: boolean;
  useMetric?: boolean;
  hideWeightDemographics?: boolean;
  ethnicityReferenceEquationLabels?: boolean;
}

export const TrialsDemographicsTable: React.FC<TrialsDemographicsTableProps> = ({ demographics, useMetric, dateFormat, disableEthnicity, birthdateDisplay, hideWeightDemographics, ethnicityReferenceEquationLabels }) => {
  const statsText = useAppSelector(state => state.translations, shallowEqual);
  let stats;

  if (demographics !== undefined) {
    stats = {
      name: demographics?.name || '',
      gender: demographics?.gender === "MALE" ? statsText.male : statsText.female,
      birth: moment.utc(Number(demographics.birth)).format(dateFormat.toUpperCase()),
      heightInches: (Number(demographics.heightInches)).toFixed(2),
      heightCm: (convertInchesToCentimeters(demographics.heightInches)).toFixed(2),
      weightLbs: (Number(demographics.weightLbs)).toFixed(2) || '',
      weightKg: (convertPoundsToKilograms(demographics.weightLbs)).toFixed(2),
      ethnicity: statsText.ethnicities[demographics.ethnicity as keyof typeof statsText.ethnicities],
    };
  };

  return (<>
    {demographics && !isEmptyObjectOrArray(demographics)
      ? <Container className="table-stats">
        <Table size='sm' className='pb-0 mb-0' borderless>
          <tbody>
            {!isNaN(Number(stats.heightCm)) && !isNaN(Number(stats.weightKg)) && <>
              <tr>
                <td>{statsText.sexAtBirth}</td>
                <td style={{ textAlign: 'right' }}>{stats.gender}</td>
              </tr>
              <tr>
                <td>{statsText.birth}</td>
                <td style={{ textAlign: 'right' }}>{(birthdateDisplay === 'birthYearOnly' ? stats.birth.split('/').filter(x => x.length > 2)[0] : birthdateDisplay === 'monthAndYear' ? `${moment.utc(Number(demographics.birth)).format(dateFormat.toUpperCase().split('/').filter(dateType => dateType.toUpperCase() !== 'DD').join('/'))}` : stats.birth)}</td>
              </tr>
              <tr>
                <td>{statsText.height}</td>
                <td style={{ textAlign: 'right' }}>{(useMetric ? `${stats.heightCm}cm` : `${stats.heightInches}in`)}</td>
              </tr>
              {!hideWeightDemographics &&
                <tr>
                  <td>{statsText.weight}</td>
                  <td style={{ textAlign: 'right' }}>{useMetric ? `${stats.weightKg}kg` : `${stats.weightLbs}lbs`}</td>
                </tr>
              }
              {!disableEthnicity &&
                <tr>
                  <td>{ethnicityReferenceEquationLabels
                    ? (statsText.referenceEquations ?? "Reference Equations")
                    : statsText.ethnicity}</td>
                  <td style={{ textAlign: 'right' }}>{resolveEthnicityLabel(
                    demographics.ethnicity,
                    ethnicityMap[stats.ethnicity?.toUpperCase()] || stats.ethnicity,
                    ethnicityReferenceEquationLabels,
                  )}</td>
                </tr>}
            </>}
          </tbody>
        </Table>
      </Container>
      : <Container className="table-stats">
        <Table size='sm' className='pb-0 mb-0' borderless>
          <tbody>
            <tr>
              <td>{statsText.sexAtBirth}</td>
              <td style={{ textAlign: 'right' }}>{'--'}</td>
            </tr>
            <tr>
              <td>{statsText.birth}</td>
              <td style={{ textAlign: 'right' }}>{'--'}</td>
            </tr>
            <tr>
              <td>{statsText.height}</td>
              <td style={{ textAlign: 'right' }}>{'--'}</td>
            </tr>
            {!hideWeightDemographics &&
              <tr>
                <td>{statsText.weight}</td>
                <td style={{ textAlign: 'right' }}>{'--'}</td>
              </tr>
            }
            {!disableEthnicity && <tr>
              <td>{ethnicityReferenceEquationLabels
                ? (statsText.referenceEquations ?? "Reference Equations")
                : statsText.ethnicity}</td>
              <td style={{ textAlign: 'right' }}>{'--'}</td>
            </tr>}
          </tbody>
        </Table>
      </Container>}
  </>)
};